import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Building, Lock, Users } from '@digital-twins/design-system/icons'

import { CollaboratorList } from '@/components/sharing/CollaboratorList'
import { MemberSearchInput } from '@/components/sharing/MemberSearchInput'
import { ShareModalShell } from '@/components/sharing/ShareModalShell'
import {
  GeneralAccessSelect,
  ShareAccessMode,
  ShareAccessOption,
} from '@/components/sharing/ShareAccessSections'
import { useShareSettings } from '@/components/sharing/useShareSettings'
import { ShareSettingsAdapter } from '@/components/sharing/ShareSettingsTypes'

export interface CollectionShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** From `useCollectionShareAdapter`. */
  adapter: ShareSettingsAdapter
  title?: string
}

/**
 * Who can see a collection (D2): Only me, Specific people (org members
 * picked here) or Whole org. Built from the platform share pieces and
 * driven by `useShareSettings`; collections offer no guests and no public
 * link, so neither appears.
 */
export function CollectionShareDialog(props: CollectionShareDialogProps) {
  const { open, onOpenChange, adapter } = props

  const { t } = useTranslation('app')
  const share = useShareSettings({ adapter, open })

  const accessOptions: Array<ShareAccessOption> = useMemo(
    () => [
      {
        value: 'organization',
        label: t('library.collectionSharing.organization'),
        description: t('library.collectionSharing.organizationHint'),
        icon: Building,
      },
      {
        value: 'shared',
        label: t('library.collectionSharing.shared'),
        description: t('library.collectionSharing.sharedHint'),
        icon: Users,
      },
      {
        value: 'private',
        label: t('library.collectionSharing.private'),
        description: t('library.collectionSharing.privateHint'),
        icon: Lock,
      },
    ],
    [t],
  )

  function onAccessChange(mode: ShareAccessMode) {
    void share.handleSelectMode(mode)
  }

  const peopleSection =
    share.sharingMode === 'shared' ? (
      <div className="flex flex-col gap-3 px-6 pb-5" data-testid="collection-share-people">
        <MemberSearchInput
          members={share.orgMembers}
          excludeUserIds={share.excludedUserIds}
          onSelect={(member) => void share.handleAddCollaborator(member)}
          placeholder={t('library.collectionSharing.addPeople')}
          addLabel={t('library.collectionSharing.add')}
        />
        <CollaboratorList
          collaborators={share.collaborators}
          onRemove={(id) => void share.handleRemoveCollaborator(id)}
        />
      </div>
    ) : null

  const footer = (
    <div className="flex shrink-0 justify-end border-t border-border px-6 py-4">
      <button
        type="button"
        onClick={() => onOpenChange(false)}
        className="inline-flex items-center rounded-md bg-[hsl(var(--share-action))] px-4 py-2 text-sm font-medium text-[hsl(var(--share-action-foreground))] transition-all hover:brightness-110 active:scale-[0.98]"
        data-testid="collection-share-done"
      >
        {t('done', { ns: 'common' })}
      </button>
    </div>
  )

  return (
    <ShareModalShell
      open={open}
      onOpenChange={onOpenChange}
      title={props.title ?? t('library.collectionSharing.title')}
      loading={share.loading}
      loadError={share.loadError}
      onRetry={() => void share.loadData()}
      footer={footer}
      testId="collection-share-dialog"
    >
      <div className="flex flex-col gap-2 px-6 pb-4 pt-3">
        <GeneralAccessSelect
          value={share.sharingMode}
          options={accessOptions}
          onChange={onAccessChange}
          disabled={share.saving}
        />
      </div>
      {peopleSection}
    </ShareModalShell>
  )
}
